import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { sprayService, SprayCalibration, SprayMachine } from '../../services/sprayService';
import CalibrationList from './CalibrationList';
import CalibrationModal from './CalibrationModal';
import { cloneCalibration } from '../../utils/cloneCalibration';

interface Props {
  machineId: string;
  machineName: string;
}

const MachineCalibrationsTab: React.FC<Props> = ({ machineId, machineName }) => {
  const { t } = useTranslation();
  const [calibrations, setCalibrations] = useState<SprayCalibration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCalibration, setEditingCalibration] = useState<SprayCalibration | undefined>(undefined);

  const loadCalibrations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Always fetch with includeDeleted=true so the "Show deleted" toggle
      // in CalibrationList can reveal soft-deleted rows without a re-fetch
      // round-trip. Track Phase 5.
      const data = await sprayService.getCalibrations(machineId, { includeDeleted: true });
      setCalibrations(Array.isArray(data) ? data : []);
    } catch {
      setError(t('spray.machines.calibrations.loadError'));
    } finally {
      setLoading(false);
    }
  }, [machineId, t]);

  useEffect(() => {
    loadCalibrations();
  }, [loadCalibrations]);

  const handleAdded = (updatedMachine: SprayMachine) => {
    setShowModal(false);
    setEditingCalibration(undefined);
    if (updatedMachine.calibrations) {
      setCalibrations(updatedMachine.calibrations);
    } else {
      loadCalibrations();
    }
  };

  const handleEdit = (calibration: SprayCalibration) => {
    setEditingCalibration(calibration);
    setShowModal(true);
  };

  const handleClone = (source: SprayCalibration) => {
    // Clone the source values but strip _id so the modal opens in Add
    // mode pre-filled. The PDF, signed-off-by, and edit history are
    // intentionally not carried over.
    setEditingCalibration(cloneCalibration(source));
    setShowModal(true);
  };

  const handleAddClick = () => {
    setEditingCalibration(undefined);
    setShowModal(true);
  };

  return (
    <div className="calibration-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="h5 mb-0">{t('spray.machines.calibrations.title', { machineName })}</h3>
        <Button variant="primary" size="sm" onClick={handleAddClick}>
          {t('spray.machines.calibrations.addCalibration')}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" role="status" />
        </div>
      ) : (
        <CalibrationList
          calibrations={calibrations}
          machineId={machineId}
          onUpdated={handleAdded}
          onEdit={handleEdit}
          onClone={handleClone}
        />
      )}

      {showModal && (
        <CalibrationModal
          machineId={machineId}
          calibration={editingCalibration}
          onClose={() => { setShowModal(false); setEditingCalibration(undefined); }}
          onSaved={handleAdded}
        />
      )}
    </div>
  );
};

export default MachineCalibrationsTab;
